import type { CityConfig } from "../city-template/types";

export const arlingtonHeightsConfig: CityConfig = {
  city: "Arlington Heights",
  state: "Illinois",
  stateAbbr: "IL",
  slug: "arlington-heights",
  route: "/cities/arlington-heights",
  title: "Emergency Passport Arlington Heights IL | Expedited Passport Services | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Arlington Heights, Illinois — as fast as 24 hours. Fast passport renewal and new passport applications for Arlington Heights, Mount Prospect, Rolling Meadows, and all northwest Cook County residents. A+ BBB rated.",
  h1: "Emergency & Expedited Passport Services in Arlington Heights, IL",
  heroSubtitle: "Arlington Heights family vacation to Ireland with 10 days notice? Northwest Community Hospital physician traveling to a medical conference in Tokyo? Metra commuter with an expired passport and a London trip next week? We help Arlington Heights residents get their passports fast — as fast as 24 hours. A+ BBB rated registered State Dept. courier serving the northwest suburbs.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Arlington%20Heights%20Illinois%20beautiful%20northwest%20Chicago%20suburb%20featuring%20historic%20downtown%20district%20with%20Arlington%20Town%20Square%2C%20Metra%20commuter%20train%20station%2C%20tree-lined%20residential%20streets%20with%20classic%20suburban%20homes%2C%20Northwest%20Community%20Hospital%20area%2C%20warm%20afternoon%20sunlight%2C%20professional%20real%20estate%20and%20cityscape%20photography%2C%20affluent%20Midwestern%20suburban%20lifestyle%20atmosphere&width=1800&height=700&seq=arlington-heights-hero-001&orientation=landscape",
  heroImageAlt: "Arlington Heights Illinois northwest Chicago suburb with historic downtown district and Metra station",
  geoPosition: "42.0884;-87.9806",
  geoPlacename: "Arlington Heights, Illinois",
  lastModified: "2026-06-04",
  regionalAgency: {
    name: "Chicago Passport Agency",
    address: "101 W Congress Pkwy, 4th Floor, Chicago, IL 60605",
    distance: "Approximately 35 minutes southeast of Arlington Heights via I-90 and I-290",
  },
  nearbyCitiesZips: [
    { name: "Arlington Heights", zips: "60004, 60005, 60006" },
    { name: "Mount Prospect", zips: "60056" },
    { name: "Rolling Meadows", zips: "60008" },
    { name: "Palatine", zips: "60067, 60074" },
    { name: "Buffalo Grove", zips: "60089" },
    { name: "Prospect Heights", zips: "60070" },
    { name: "Wheeling", zips: "60090" },
    { name: "Des Plaines", zips: "60016, 60017, 60018" },
    { name: "Schaumburg", zips: "60173, 60193, 60194, 60195" },
    { name: "Elk Grove Village", zips: "60007" },
    { name: "Long Grove", zips: "60047" },
    { name: "Barrington", zips: "60010, 60011" },
    { name: "Inverness", zips: "60067" },
    { name: "Northbrook", zips: "60062" },
    { name: "Glenview", zips: "60025, 60026" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier to Chicago Passport Agency — no 35-minute drive required" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-hospital-line", title: "Healthcare Travel Hub", desc: "Serving Northwest Community Hospital physicians and medical conference travelers" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels — no trip to downtown Chicago required" },
  ],
  internalLinks: [
    { name: "Expedited passport in Schaumburg, IL", route: "/states/illinois" },
    { name: "Emergency passport help in Chicago, IL", route: "/cities/chicago" },
    { name: "Fast passport renewal in Palatine, IL", route: "/states/illinois" },
    { name: "Illinois passport services overview", route: "/states/illinois" },
  ],
  fpcOfficeAddress: "101 W Congress Pkwy, 4th Floor, Chicago, IL 60605",
  fpcOfficeNote: "Fast Passport Center serves Arlington Heights, Illinois remotely via secure registered courier. Our registered couriers hand-deliver your documents to the Chicago Passport Agency (35 minutes southeast via I-90) — without you making the trip. Arlington Heights residents can ship from any UPS or FedEx in the northwest suburbs.",
  fpcOfficeDistance: "Serving Arlington Heights, Mount Prospect, Rolling Meadows, Palatine, Buffalo Grove, Prospect Heights, Wheeling, Des Plaines, Schaumburg, Elk Grove Village, Long Grove, Barrington, Inverness, Northbrook, and all northwest Cook County suburbs",
  serviceAreaIntro: "Fast Passport Center provides emergency and expedited passport services throughout Arlington Heights and the northwest suburbs. From the historic downtown Arlington Town Square and Northwest Community Hospital to the quiet family neighborhoods off Euclid Avenue, from the Metra station commuter community to the corporate offices near Arlington Park, from Mount Prospect's Randhurst area to the tree-lined streets of Palatine — we serve all of northwest Cook County and beyond.",
  nearbyCitiesIntro: "We serve Arlington Heights and all nearby communities including Mount Prospect, Rolling Meadows, Palatine, Buffalo Grove, Prospect Heights, Wheeling, Des Plaines, Schaumburg, Elk Grove Village, Long Grove, Barrington, Inverness, Northbrook, and Glenview. We also serve all northwest Cook County and eastern Lake County communities.",
  howItWorksIntro: "Most Arlington Heights travelers complete everything online and ship documents overnight. No need to drive 35 minutes to downtown Chicago — our couriers handle the Chicago Passport Agency trip for you.",
  mistakesIntro: "Arlington Heights is one of the most desirable communities in the northwest suburbs with strong family international travel and a major healthcare employer. Here are the most common passport mistakes we see from Arlington Heights and northwest suburban travelers.",
  testimonialsIntro: "Arlington Heights travelers — from Northwest Community Hospital physicians and medical conference attendees to Metra commuters and northwest suburban families — trust us when their departure is days away.",
  faqIntro: "Have questions about expedited passports in Arlington Heights, IL? Here are the most common ones we hear from northwest suburban travelers.",
  statePageRoute: "/states/illinois",
  statePageName: "Illinois",
  dmvWebsite: "https://www.ilsos.gov/facilities/facilitylist.html",
  dmvLabel: "Illinois Secretary of State — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Must drive 35 minutes to downtown Chicago", "Full day commitment — parking and downtown navigation"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Chicago Passport Agency — hand-delivered", "No drive to downtown Chicago required", "Perfect for medical conferences and family vacations", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Chicago Passport Agency — hand-delivered", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For Arlington Heights travelers with immediate departure needs — as fast as 24 hours. Our couriers use the Chicago Passport Agency — 35 minutes away but you never make the trip.",
      bullets: ["24–48 hour turnaround available", "Chicago Passport Agency direct courier", "Northwest suburban family and medical travel support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for Arlington Heights adults with a passport issued within the last 15 years. Skip the post office appointment entirely.",
      bullets: ["No clerk visit required for most renewals", "Mail-in or courier options", "Returned to your Arlington Heights address"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport (First-Time)",
      description: "First-time passport application for Arlington Heights residents — including Northwest Community Hospital staff, new citizens, and northwest suburban families.",
      bullets: ["Acceptance facility scheduling", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Emergency passport replacement for Arlington Heights residents who lost their passport before international travel or a medical conference.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement in 24–48 hours"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 — for Arlington Heights and northwest suburban families with kids traveling internationally.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change Passport",
      description: "Passport name correction after marriage, divorce, or legal name changes for Arlington Heights and northwest suburban residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Arlington Heights IL", description: "Urgent passport processing for Arlington Heights Illinois travelers with immediate departure needs." },
    { name: "Expedited Passport Renewal Arlington Heights IL", description: "Fast passport renewal for Arlington Heights Illinois residents with a previously-issued passport." },
    { name: "First-Time Passport Arlington Heights IL", description: "New passport application services for Arlington Heights Illinois residents applying for their first passport." },
    { name: "Child Passport Arlington Heights IL", description: "Minor passport services for Arlington Heights Illinois families with children under 16." },
  ],
  testimonials: [
    {
      name: "Dr. Karen M.",
      location: "Arlington Heights, IL",
      rating: 5,
      text: "Medical conference in Tokyo with 7 days notice. My passport had expired during residency. Fast Passport Center had my renewal in 36 hours. Shipped from the UPS near the hospital and everything was handled perfectly. Saved my presentation at the conference.",
      service: "Emergency Passport Renewal",
    },
    {
      name: "The Sullivan Family",
      location: "Arlington Heights, IL",
      rating: 5,
      text: "Family reunion in Ireland — 4 passports needed. Two adult renewals, one new, one child passport. Fast Passport Center got all four done in 5 days. The dedicated agent was incredible with our kids' documentation. We shipped from FedEx on Rand Road.",
      service: "Family Passport Bundle",
    },
    {
      name: "Steven P.",
      location: "Mount Prospect, IL",
      rating: 5,
      text: "Business trip to Germany with 9 days notice. I'm a daily Metra commuter and couldn't take a day off to go to Chicago. Fast Passport Center had my passport in 3 days. The online process meant zero time off work. Perfect for busy professionals.",
      service: "New Passport",
    },
  ],
  facilities: [
    {
      id: "arh-1",
      name: "Arlington Heights Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "909 W Euclid Ave",
      city: "Arlington Heights",
      state: "IL",
      zip: "60005",
      phone: "(847) 398-3450",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Central Arlington Heights, near Northwest Community Hospital",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary Arlington Heights acceptance facility. Appointments required — book 1–2 weeks in advance. Convenient to downtown Arlington Town Square.",
      icon: "ri-mail-line",
    },
    {
      id: "arh-2",
      name: "Mount Prospect Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1200 W Central Rd",
      city: "Mount Prospect",
      state: "IL",
      zip: "60056",
      phone: "(847) 253-7147",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Mount Prospect, approximately 4 miles east of Arlington Heights",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Mount Prospect, Prospect Heights. Near Randhurst shopping area.",
      icon: "ri-mail-line",
    },
    {
      id: "arh-3",
      name: "Palatine Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "35 W Wood St",
      city: "Palatine",
      state: "IL",
      zip: "60067",
      phone: "(847) 358-1635",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Palatine, approximately 5 miles north of Arlington Heights",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Palatine, northern Arlington Heights. Near Palatine Metra station.",
      icon: "ri-mail-line",
    },
    {
      id: "arh-4",
      name: "Buffalo Grove Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "250 W Dundee Rd",
      city: "Buffalo Grove",
      state: "IL",
      zip: "60089",
      phone: "(847) 520-0199",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Buffalo Grove, approximately 7 miles north of Arlington Heights",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Buffalo Grove, Long Grove, and northern Arlington Heights residents.",
      icon: "ri-mail-line",
    },
    {
      id: "arh-5",
      name: "Rolling Meadows Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "3100 Kirchoff Rd",
      city: "Rolling Meadows",
      state: "IL",
      zip: "60008",
      phone: "(847) 253-0420",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Rolling Meadows, approximately 4 miles west of Arlington Heights",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Rolling Meadows and western Arlington Heights. Good alternative for south Arlington Heights residents.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, 2–3 week wait", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 35 min drive to downtown Chicago", fpc: "No — ship from Arlington Heights" },
    { feature: "Parking & Navigation", agency: "Downtown Chicago parking costs", fpc: "Ship from your neighborhood" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
  ],
  mistakes: [
    {
      icon: "ri-calendar-close-line",
      title: "Medical Conference Short Notice",
      description: "Northwest Community Hospital physicians often get conference acceptance 1–2 weeks before travel. Standard passport processing takes 6–13 weeks. Our courier service bridges the gap with 24–48 hour turnaround for medical professionals.",
    },
    {
      icon: "ri-image-line",
      title: "Wrong Passport Photo",
      description: "Local photo services near downtown Arlington Heights sometimes produce photos that meet ID requirements but fail strict passport standards. Photos must be exactly 2x2 inches on a white background, no shadows, neutral expression.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or incorrect form type cause rejections at Arlington Heights area acceptance facilities. First-time applicants especially need to verify they are using DS-11, not DS-82.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent for Minors",
      description: "Both parents must appear or submit notarized consent for child passports. Northwest suburban families often arrive with only one parent — causing same-day rejection and delays.",
    },
    {
      icon: "ri-road-map-line",
      title: "Driving to Chicago Agency Unnecessarily",
      description: "Many Arlington Heights residents attempt the drive to the Chicago Passport Agency in an emergency. The 35-minute drive plus downtown parking means a half-day commitment. Our courier service makes the trip for you.",
    },
    {
      icon: "ri-checkbox-line",
      title: "Assuming 4–6 Weeks Is Enough",
      description: "Routine processing takes 6–13 weeks. If your travel is within 10 weeks, use expedited service. Summer family travel from northwest suburbs peaks June–August — plan ahead.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes — from anywhere in Arlington Heights or the northwest suburbs." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS or FedEx near Arlington Town Square or Northwest Community Hospital." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy — experienced with medical travel and family vacation documentation." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Our registered courier submits to the Chicago Passport Agency — just 35 minutes away but you never make the trip." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Arlington Heights address — trackable every step of the way." },
  ],
  faqs: [
    { q: "How fast can I get an emergency passport in Arlington Heights, IL?", a: "Fast Passport Center offers emergency passport processing in Arlington Heights, Illinois in as little as 24–48 hours. Our registered couriers hand-deliver your documents to the Chicago Passport Agency (35 minutes southeast via I-90) — without you making the trip." },
    { q: "Does Arlington Heights have its own passport agency?", a: "No — Arlington Heights does not have a regional passport agency. The nearest facility is the Chicago Passport Agency (approximately 35 minutes southeast via I-90 and I-290). Fast Passport Center's courier service means Arlington Heights residents never need to make the trip." },
    { q: "Can I get an expedited passport in Mount Prospect, Palatine, or Rolling Meadows?", a: "Absolutely. Fast Passport Center serves all of the northwest suburbs including Mount Prospect, Palatine, Rolling Meadows, Buffalo Grove, Prospect Heights, Wheeling, Des Plaines, and all northwest Cook County communities. Our service works the same way everywhere." },
    { q: "Does Fast Passport Center serve Northwest Community Hospital?", a: "Yes. Fast Passport Center serves physicians, staff, and medical professionals at Northwest Community Hospital. We understand medical conference schedules, short-notice travel, and demanding professional schedules. Our courier service is ideal for healthcare workers who can't take a day off to visit the Chicago Passport Agency." },
    { q: "What airport do Arlington Heights travelers use for international flights?", a: "Chicago O'Hare International Airport (ORD) is the primary airport for Arlington Heights and northwest suburban travelers. O'Hare is approximately 20 minutes south via I-90 and I-294 and offers direct international flights worldwide." },
    { q: "Where can I get passport photos in Arlington Heights?", a: "Walgreens, CVS, and UPS Store locations throughout Arlington Heights, Mount Prospect, and Palatine offer passport photos. FedEx Office in Schaumburg also provides them. Photos must be 2x2 inches on a white background, taken within 6 months." },
    { q: "Can I get a passport for my child in Arlington Heights?", a: "Yes. Fast Passport Center provides child passport services for minors under 16 throughout Arlington Heights and the northwest suburbs. Both parents or legal guardians must typically be present at the acceptance facility, and we guide you through the entire process." },
    { q: "How much does an expedited passport cost in Arlington Heights, IL?", a: "Government fees are fixed: $130 for a first-time adult passport book or $130 + $60 expedite fee for a renewed adult passport. Fast Passport Center's service fees vary by processing speed. Contact us for a quote based on your specific departure timeline." },
  ],
  photoLocations: [
    { name: "Walgreens — Arlington Heights", address: "1710 W Campbell St, Arlington Heights, IL 60005", hours: "Mon–Sun: 7AM–10PM" },
    { name: "UPS Store — Arlington Heights", address: "41 W Rand Rd, Arlington Heights, IL 60004", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "CVS — Mount Prospect", address: "100 W Rand Rd, Mount Prospect, IL 60056", hours: "Mon–Sun: 8AM–10PM" },
    { name: "FedEx Office — Palatine", address: "221 E Northwest Hwy, Palatine, IL 60067", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
  ],
  dmvLocations: [
    { name: "Illinois SOS — Des Plaines", address: "1470 Lee St, Des Plaines, IL 60018", hours: "Mon–Fri: 8:00AM–5:00PM", note: "Illinois Secretary of State for driver's licenses, REAL ID, and vehicle registration. Serves Arlington Heights, Mount Prospect, and Des Plaines. Online reservations strongly recommended.", featured: true },
    { name: "Illinois SOS — Schaumburg", address: "1227 E Golf Rd, Schaumburg, IL 60173", hours: "Mon–Fri: 8:00AM–5:00PM", note: "Serves Schaumburg, Palatine, and western Arlington Heights area. Full REAL ID processing available.", featured: false },
  ],
  airports: [
    {
      code: "ORD",
      name: "Chicago O'Hare International Airport",
      distance: "Approximately 20 minutes south via I-90 and I-294",
      address: "10000 W O'Hare Ave, Chicago, IL 60666",
      note: "Primary international airport for Arlington Heights and northwest suburban travelers. One of the world's busiest airports with direct flights worldwide. United and American Airlines hub. Extremely convenient — just a straight shot down Route 53/I-90.",
      flightUrl: "https://www.flychicago.com/ohare/",
      flightLabel: "ORD Flight Info",
      featured: true,
    },
    {
      code: "MDW",
      name: "Chicago Midway International Airport",
      distance: "Approximately 45 minutes southeast via I-290 and I-55",
      address: "5700 S Cicero Ave, Chicago, IL 60638",
      note: "Southwest Airlines hub with domestic connections. Good for travelers flying Southwest to connect to international destinations.",
      flightUrl: "https://www.flychicago.com/midway/",
      flightLabel: "MDW Flight Info",
      featured: false,
    },
  ],
};